import '@theme/fastutv-shopping-context';
import {getSearchRuntime} from '@theme/fastutv-search-runtime';
import {syncSearchInputs,buildSearchIntent,readLocalSearchQuery,searchVehicleLabel,parseNativeYearSKUReference} from '@theme/fastutv-search-intent';

export function localFormVehicle(query,fallback='all') {
  const intent=buildSearchIntent(String(query||''));
  if(intent.conflictingMake||intent.advanced)return 'all';
  return searchVehicleLabel(intent)||fallback||'all';
}

export function validatedLocalYearSKUReference(query,kind,value,origin='https://www.fastutv.com') {
  if(value===undefined||value===null)return null;
  const invalid=()=>{const error=new Error('LOCAL_SEARCH_CLASSIFICATION');error.code='LOCAL_SEARCH_CLASSIFICATION';throw error;};
  if(kind!=='words'||!value||typeof value!=='object'||Array.isArray(value)||Object.keys(value).length!==3||!Object.keys(value).every(key=>['version','year','skuKey'].includes(key))||typeof value.skuKey!=='string')invalid();
  const expected=parseNativeYearSKUReference(readLocalSearchQuery(query,origin),new Set([value.skuKey]));
  if(!expected||value.version!==expected.version||value.year!==expected.year||value.skuKey!==expected.skuKey)invalid();
  return value;
}

export function validateLocalClassificationRouting(query,fallback,kind,origin='https://www.fastutv.com',reference=null) {
  const invalid=code=>{const error=new Error(code);error.code=code;throw error;};
  const raw=readLocalSearchQuery(query,origin),boundReference=validatedLocalYearSKUReference(raw,kind,reference,origin),intent=['sku','title'].includes(kind)||boundReference?null:buildSearchIntent(raw);
  if(intent?.advanced)invalid('UNSUPPORTED_ADVANCED_QUERY');
  // The old form search used preferQueryVehicle:true. Keep its validation
  // boundary after literal SKU priority, without calculating result cohorts.
  const vehicle=intent&&(intent.model||intent.make)?'':fallback;
  if(vehicle&&vehicle!=='all'){
    if(typeof vehicle!=='string'||vehicle.length>200)invalid('UNSUPPORTED_VEHICLE');
    const scoped=buildSearchIntent(vehicle);
    if(scoped.advanced||scoped.residual||scoped.category||!(scoped.model||scoped.make||scoped.vehicleConstraint))invalid('UNSUPPORTED_VEHICLE');
  }
}

export function buildLocalSearchURL(action,query,vehicle,{currentURL,rootPath='/',themeId='189256892584',prefix=true}={}) {
  const current=new URL(currentURL),url=new URL(action,current);
  if(url.origin!==current.origin||!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?search\/?$/i.test(url.pathname))throw new Error('UNSUPPORTED_SEARCH_DESTINATION');
  const path=rootPath.endsWith('/')?rootPath:rootPath+'/';url.pathname=path+'search';url.search='';url.hash='';
  url.searchParams.set('q',String(query??'').trim().replace(/\s+/g,' '));url.searchParams.set('type','product');
  url.searchParams.set('options[prefix]',prefix?'last':'none');
  url.searchParams.set('fastutv_vehicle',String(vehicle||'all'));
  const preview=current.searchParams.getAll('preview_theme_id');
  if(preview.length>1||preview.length===1&&preview[0]!==String(themeId))throw new Error('INVALID_DRAFT_PREVIEW');
  if(preview.length)url.searchParams.set('preview_theme_id',preview[0]);
  return url;
}
export function installLocalSearchForms({document:doc=document,window:win=window}={}) {
  if(doc.documentElement.dataset.fastutvLocalSearch!=='enabled'||doc.documentElement.hasAttribute('data-local-search-forms'))return;
  doc.documentElement.setAttribute('data-local-search-forms','');
  const current=()=>new URL(win.location.href);
  function config(){try{return JSON.parse(doc.querySelector('[data-fastutv-local-search-config]').textContent);}catch{return null;}}
  function searchForm(target){
    const form=target?.closest?.('form');if(!form?.action)return null;
    let url;try{url=new URL(form.action,win.location.href);}catch{return null;}
    if(url.origin!==win.location.origin||!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?search\/?$/i.test(url.pathname))return null;
    const input=form.querySelector('input[name="q"]');return input?{form,input}:null;
  }
  let warmPromise;
  const warm=()=>{
    if(current().searchParams.has('fastutv_algolia_test'))return;
    let runtime;try{runtime=getSearchRuntime();if(!runtime||!['empty','expired','loading'].includes(runtime.getStatus().state))return;}catch{return;}if(warmPromise)return;
    warmPromise=runtime.warmup().catch(()=>{}).finally(()=>{warmPromise=null;});
  };
  let submission;
  win.addEventListener('submit',async event=>{
    if(current().searchParams.has('fastutv_algolia_test'))return;
    const found=searchForm(event.target);if(!found)return;
    const c=config();if(!c?.enabled)return;
    event.preventDefault();event.stopImmediatePropagation();
    const selectedVehicle=()=>found.form.querySelector('input[name="fastutv_vehicle"]')?.value||doc.documentElement.dataset.fastutvVehicle||'all';
    const raw=found.input.value,fallback=selectedVehicle(),href=win.location.href,documentVehicle=doc.documentElement.dataset.fastutvVehicle;
    const destination={currentURL:href,rootPath:c.rootPath,themeId:c.themeId};
    // Reject unsupported destinations before any source classification.
    try{buildLocalSearchURL(found.form.action,raw,fallback,destination);}
    catch{found.input.setCustomValidity?.(c.strings?.local_error||'Search could not open this destination.');found.input.reportValidity?.();found.input.addEventListener?.('input',()=>found.input.setCustomValidity?.(''),{once:true});return;}
    submission?.cancel();
    const controller=new AbortController(),pending={controller};
    let status=found.form.querySelector('[data-fastutv-search-submit-status]');
    if(!status){status=doc.createElement('p');status.setAttribute('data-fastutv-search-submit-status','');status.setAttribute('role','status');status.setAttribute('aria-live','polite');found.form.append(status);}
    status.hidden=false;status.textContent=c.strings?.local_loading||'Finding matching products…';found.form.setAttribute('aria-busy','true');delete found.form.dataset.searchError;
    const stale=()=>controller.signal.aborted||submission!==pending||found.form.isConnected===false||found.input.value!==raw||selectedVehicle()!==fallback||doc.documentElement.dataset.fastutvVehicle!==documentVehicle||win.location.href!==href;
    pending.cancel=()=>{controller.abort();if(submission!==pending)return;submission=null;found.form.removeAttribute('aria-busy');status.hidden=true;found.input.setCustomValidity?.('');};
    submission=pending;
    found.input.addEventListener('input',pending.cancel,{signal:controller.signal});
    doc.addEventListener('fastutv:vehicle-change',pending.cancel,{signal:controller.signal});
    for(const name of ['pagehide','popstate'])win.addEventListener(name,pending.cancel,{signal:controller.signal});
    try{
      const runtime=getSearchRuntime();if(!runtime)throw new Error('LOCAL_SEARCH_DISABLED');
      // Only the current complete source can distinguish an original literal
      // SKU from shopper model/fuel/operator words. No SKU heuristics or copy.
      const observe={signal:controller.signal,onProgress:progress=>{
        if(stale())return;const loaded=Number(progress?.loadedProducts),total=Number(progress?.totalProducts);
        if(Number.isSafeInteger(loaded)&&Number.isSafeInteger(total)&&total>0)status.textContent=(c.strings?.local_loading||'Finding matching products…')+' ('+loaded.toLocaleString()+'/'+total.toLocaleString()+')';
      }};
      const result=typeof runtime.classifyQuery==='function'?await runtime.classifyQuery({query:raw},observe):
        await runtime.search({query:raw,vehicle:fallback,preferQueryVehicle:true,page:1,pageSize:1},observe);
      if(stale())return;
      if(result?.complete!==true||!['sku','title','words'].includes(result.queryKind))throw new Error('LOCAL_SEARCH_CLASSIFICATION');
      const reference=validatedLocalYearSKUReference(raw,result.queryKind,result.nativeYearSKUReference,c.origin||win.location.origin);
      validateLocalClassificationRouting(raw,fallback,result.queryKind,c.origin||win.location.origin,reference);
      const vehicle=['sku','title'].includes(result.queryKind)||reference?fallback:localFormVehicle(raw,fallback);
      const next=buildLocalSearchURL(found.form.action,raw,vehicle,destination);
      status.hidden=true;
      const local=doc.querySelector('fastutv-discovery[data-kind="search"]');
      if(local&&/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?search\/?$/i.test(win.location.pathname)){
        win.history.pushState({},'',next);syncSearchInputs(doc,next);
        found.form.closest('dialog-component')?.closeDialog?.();
        doc.dispatchEvent(new CustomEvent('fastutv:local-search-navigation',{detail:{url:next.href}}));
      }else win.location.assign(next.href);
    }catch(error){
      if(stale())return;
      status.textContent=c.strings?.local_error||'Search couldn’t load. Try again.';
      found.form.dataset.searchError=String(error?.code||'SEARCH_UNAVAILABLE').replace(/[^A-Z0-9_]/gi,'_').slice(0,90);
    }finally{
      if(submission===pending){if(stale())status.hidden=true;submission=null;found.form.removeAttribute('aria-busy');controller.abort();}
    }
  },true);
  doc.addEventListener('focusin',event=>{if(searchForm(event.target))warm();});
  // Focus and submission express search intent. The search surface owns its
  // normal load; unrelated PDP/home documents do not download the catalog idle.
}
if(typeof document!=='undefined'&&typeof window!=='undefined')installLocalSearchForms();
